import { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import { analyticsReducer, initialState } from '../reducer/analyticsReducer';
import {
	fetchDashboardStart, fetchDashboardSuccess, fetchDashboardError,
	fetchFilteredStart,  fetchFilteredSuccess,  fetchFilteredError,
	fetchHourlyStart,    fetchHourlySuccess,    fetchHourlyError,
	fetchRolesSuccess,
	setDaysFilter, setTopN, setModuleFilter, setUserFilter, setRoleFilter,
	setDateFilter, clearFilter, clearDrillDown, clearDateFilter,
	trackAccessSuccess,
} from '../actions/analyticsActions';
import { GetDashboardData, GetFilteredData, GetHourlyByDate, GetRoles, PostTrackAccess } from '../requests/analyticsRequests';

const AnalyticsContext = createContext(null);
const AUTO_REFRESH_MS  = 60_000;

export function AnalyticsProvider({ children }) {
	const [state, dispatch] = useReducer(analyticsReducer, initialState);
	const refreshTimer      = useRef(null);

	// ── core filter fetch ─────────────────────────────────────
	// Always passes all three filters — backend ignores nulls
	const doFilter = useCallback(async (days, topN, moduleId, userId, roleId) => {
		dispatch(fetchFilteredStart());
		try {
			const res = await GetFilteredData(days, topN, moduleId, userId, roleId);
			dispatch(fetchFilteredSuccess(res.data));
		} catch (err) {
			dispatch(fetchFilteredError(err?.response?.data?.message ?? err?.message ?? 'Unknown error'));
		}
	}, []);

	// ── load roles once ───────────────────────────────────────
	useEffect(() => {
		GetRoles()
			.then(res => dispatch(fetchRolesSuccess(res.data)))
			.catch(() => {});
	}, []);

	// ── load global dashboard ─────────────────────────────────
	const loadDashboard = useCallback(async (days = state.days, topN = state.topN) => {
		dispatch(fetchDashboardStart());
		try {
			const res = await GetDashboardData(days, topN);
			dispatch(fetchDashboardSuccess(res.data));
		} catch (err) {
			dispatch(fetchDashboardError(err?.response?.data?.message ?? err?.message ?? 'Unknown error'));
		}
	}, [state.days, state.topN]);

	useEffect(() => { loadDashboard(state.days, state.topN); }, [state.days, state.topN]);

	useEffect(() => {
		refreshTimer.current = setInterval(() => loadDashboard(state.days, state.topN), AUTO_REFRESH_MS);
		return () => clearInterval(refreshTimer.current);
	}, [state.days, state.topN]);

	// ── change days ───────────────────────────────────────────
	const changeDays = useCallback((days) => {
		dispatch(setDaysFilter(days));
		const { filterType, selectedModule, selectedUser, selectedRole, topN } = state;
		if (filterType && filterType !== null) {
			const moduleId = filterType === 'module' ? selectedModule?.id : null;
			const userId   = filterType === 'user'   ? selectedUser?.id   : null;
			const roleId   = selectedRole?.id ?? null;
			doFilter(days, topN, moduleId, userId, roleId);
		}
	}, [state, doFilter]);

	// ── change topN ───────────────────────────────────────────
	const changeTopN = useCallback((topN) => {
		dispatch(setTopN(topN));
	}, []);

	// ── select role ───────────────────────────────────────────
	// Clears module/user drill-down, fetches role-filtered data
	const selectRole = useCallback((role) => {
		if (state.selectedRole?.id === role.id) {
			dispatch(clearFilter());
			return;
		}
		dispatch(setRoleFilter(role));
		doFilter(state.days, state.topN, null, null, role.id);
	}, [state.selectedRole, state.days, state.topN, doFilter]);

	// ── select module ─────────────────────────────────────────
	// Combines with active role filter if present
	const selectModule = useCallback((module) => {
		const roleId = state.selectedRole?.id ?? null;

		// clicking same module again clears the drill-down (but keeps role if active)
		if (state.selectedModule?.id === module.id) {
			if (roleId) {
				dispatch(clearDrillDown());
				doFilter(state.days, state.topN, null, null, roleId);
			} else {
				dispatch(clearFilter());
			}
			return;
		}

		dispatch(setModuleFilter(module));
		doFilter(state.days, state.topN, module.id, null, roleId);
	}, [state.selectedModule, state.selectedRole, state.days, state.topN, doFilter]);

	// ── select user ───────────────────────────────────────────
	// Combines with active role filter if present
	const selectUser = useCallback((user) => {
		const roleId = state.selectedRole?.id ?? null;

		// clicking same user again clears the drill-down (but keeps role if active)
		if (state.selectedUser?.id === user.id) {
			if (roleId) {
				dispatch(clearDrillDown());
				doFilter(state.days, state.topN, null, null, roleId);
			} else {
				dispatch(clearFilter());
			}
			return;
		}

		dispatch(setUserFilter(user));
		doFilter(state.days, state.topN, null, user.id, roleId);
	}, [state.selectedUser, state.selectedRole, state.days, state.topN, doFilter]);

	// ── select date ───────────────────────────────────────────
	const selectDate = useCallback(async (date) => {
		if (state.selectedDate === date) { dispatch(clearDateFilter()); return; }
		dispatch(setDateFilter(date));
		dispatch(fetchHourlyStart());
		try {
			const moduleId = state.selectedModule?.id ?? null;
			const userId   = state.selectedUser?.id   ?? null;
			const roleId   = state.selectedRole?.id   ?? null;
			const res = await GetHourlyByDate(date, moduleId, userId, roleId);
			dispatch(fetchHourlySuccess(res.data));
		} catch (err) {
			dispatch(fetchHourlyError(err?.response?.data?.message ?? err?.message ?? 'Unknown error'));
		}
	}, [state.selectedDate, state.selectedModule, state.selectedUser, state.selectedRole]);

	// ── clear all filters ─────────────────────────────────────
	const doClearFilter = useCallback(() => { dispatch(clearFilter()); }, []);
	const doClearDate   = useCallback(() => { dispatch(clearDateFilter()); }, []);

	// ── track access ──────────────────────────────────────────
	const trackAccess = useCallback(async (moduleId, moduleName, userId) => {
		try {
			const res = await PostTrackAccess(moduleId, moduleName, userId);
			dispatch(trackAccessSuccess(res.data));
		} catch { /* fail silently */ }
	}, []);

	return (
		<AnalyticsContext.Provider value={{
			state,
			loadDashboard,
			changeDays,
			changeTopN,
			selectModule,
			selectUser,
			selectRole,
			selectDate,
			clearFilter: doClearFilter,
			clearDate:   doClearDate,
			trackAccess,
		}}>
			{children}
		</AnalyticsContext.Provider>
	);
}

export function useAnalytics() {
	const ctx = useContext(AnalyticsContext);
	if (!ctx) throw new Error('useAnalytics must be used inside <AnalyticsProvider>');
	return ctx;
}